import { Component, ViewEncapsulation, OnInit, DestroyRef, inject, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { WindowService } from 'src/shared/providers/window.service';
import { UrlStateService } from 'src/shared/services/url-state.service';
import { RentalModesService } from 'src/shared/services/rental-modes.service';

@Component({
    templateUrl: './marketing.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class MarketingPage implements OnInit {
  readonly windowService = inject(WindowService);

  private readonly route = inject(ActivatedRoute);
  private readonly urlStateService = inject(UrlStateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly rentalModes = inject(RentalModesService);

  selectedTab = 'listings';
  loaded = false;
  // Leasing is a unit-centric section: each unit has a lifecycle state
  // (Vacant → Applications → Signing → Occupied → Overdue → Eviction), and
  // rent collection lives inside the Occupied state.

  // Leasing only exists for long-term rentals — dropped when Account Settings
  // (My Account) turns the long-term toggle off.
  private static readonly LONG_TERM_TABS = ['leasing'];

  private readonly allTabs = [
    { value: 'listings', label: 'Listings', icon: 'assets/svg/swap-horizontal-outline.svg' },
    { value: 'leasing', label: 'Leasing', icon: 'assets/svg/document-text-outline.svg' },
    { value: 'upsells', label: 'Upsells', icon: 'assets/svg/trending-up-outline.svg' },
    { value: 'reviews', label: 'Reviews', icon: 'assets/svg/star-outline.svg' },
    { value: 'wifi', label: 'Wi-Fi Marketing', icon: 'assets/svg/wifi-outline.svg' },
    { value: 'happiness', label: 'Happiness', icon: 'assets/svg/star-sharp.svg' }
  ];

  tabs = this.allTabs;

  get tabTitle(): string {
    return this.tabs.find(t => t.value === this.selectedTab)?.label ?? 'Revenue';
  }

  ngOnInit() {
    this.loadPage();
    // A section with its own address (Listings) shows only that section.
    const pageTab: string | undefined = this.route.snapshot.data['tab'];
    if (pageTab) {
      this.selectedTab = pageTab;
    }

    this.rentalModes.modes$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(modes => {
        this.tabs = modes.longTerm
          ? this.allTabs
          : this.allTabs.filter(t => !MarketingPage.LONG_TERM_TABS.includes(t.value));
        // Don't strand the user on a tab that just disappeared.
        if (!this.tabs.some(t => t.value === this.selectedTab)) {
          this.selectedTab = this.tabs[0].value;
        }
        this.cdr.markForCheck();
      });

    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        if (!pageTab && params['tab'] && this.tabs.some(t => t.value === params['tab'])) {
          this.selectedTab = params['tab'];
          // OnPush: the query-param change must be flushed to the view.
          this.cdr.markForCheck();
        }
      });
  }

  loadPage() {
    this.loaded = true;
    this.cdr.markForCheck();
  }

  selectTab(tab: string) {
    this.selectedTab = tab;
    this.urlStateService.updateQueryParams({ tab });
  }

}
